'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Banknote, Loader2, RefreshCw, UserPlus } from 'lucide-react';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { trpc } from '@/lib/api/trpc';

const usd = (cents: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
const day = (value: string | Date | null | undefined) => (value ? new Date(value).toLocaleDateString() : '—');

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  requested: 'yellow',
  approved: 'blue',
  paid: 'green',
  rejected: 'red',
  cancelled: 'gray',
};

/** One open request: the bank details ops need, and the two outcomes. */
function SettlementRow({
  row,
  onDone,
}: {
  row: {
    id: string;
    status: string;
    kind: string;
    amountCents: number;
    createdAt: string | Date | null;
    referrerName: string | null;
    referrerAccountId: string;
    paymentReference: string | null;
    bankDetails: Record<string, unknown> | null;
  };
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const markPaid = trpc.referral.opsMarkTransferPaid.useMutation({
    onSuccess: () => {
      toast.success(t('accountManager.referralPayouts.paid'));
      onDone();
    },
    onError: (e) => toast.error(e.message),
  });
  const reject = trpc.referral.opsRejectTransfer.useMutation({
    onSuccess: () => {
      toast.success(t('accountManager.referralPayouts.rejected'));
      onDone();
    },
    onError: (e) => toast.error(e.message),
  });
  const open = row.status === 'requested' || row.status === 'approved';
  const bank = row.bankDetails ?? {};
  const bankLine = [
    bank.beneficiaryName,
    bank.bankName,
    bank.iban ? `IBAN ${String(bank.iban)}` : null,
    bank.swiftCode ? `SWIFT ${String(bank.swiftCode)}` : null,
    bank.accountNumber ? `Acct ${String(bank.accountNumber)}` : null,
    bank.routingNumber ? `Routing ${String(bank.routingNumber)}` : null,
    (bank.address as { country?: string } | undefined)?.country,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">{day(row.createdAt)}</TableCell>
      <TableCell>
        <div className="font-medium">{row.referrerName ?? row.referrerAccountId}</div>
        <div className="font-mono text-xs text-muted-foreground">{row.referrerAccountId}</div>
      </TableCell>
      <TableCell className="max-w-md text-xs">{row.kind === 'credits' ? t('accountManager.referralPayouts.creditsKind') : bankLine || '—'}</TableCell>
      <TableCell className="whitespace-nowrap text-right font-semibold">{usd(row.amountCents)}</TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[row.status] ?? 'gray'}>{row.status}</Badge>
        {row.paymentReference && <div className="mt-1 font-mono text-xs text-muted-foreground">{row.paymentReference}</div>}
      </TableCell>
      <TableCell>
        {open && (
          <div className="flex min-w-[260px] flex-col gap-2">
            <div className="flex gap-2">
              <Input
                placeholder={t('accountManager.referralPayouts.reference')}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="h-8 text-xs"
              />
              <Button size="sm" disabled={reference.trim().length < 3 || markPaid.isPending} onClick={() => markPaid.mutate({ settlementId: row.id, paymentReference: reference })}>
                {t('accountManager.referralPayouts.markPaid')}
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={t('accountManager.referralPayouts.rejectReason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-8 text-xs"
              />
              <Button size="sm" variant="outline" disabled={reason.trim().length < 3 || reject.isPending} onClick={() => reject.mutate({ settlementId: row.id, reason })}>
                {t('accountManager.referralPayouts.reject')}
              </Button>
            </div>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export function ReferralPayoutsTab() {
  const { t } = useTranslation('common');
  const utils = trpc.useUtils();
  const [showAll, setShowAll] = useState(false);
  const list = trpc.referral.opsListSettlements.useQuery(
    showAll ? { statuses: ['requested', 'approved', 'paid', 'rejected', 'cancelled'], limit: 200 } : undefined,
  );
  const refresh = () => utils.referral.opsListSettlements.invalidate();

  const [refereeAccountId, setRefereeAccountId] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const attribute = trpc.referral.opsAttribute.useMutation({
    onSuccess: (res) => {
      toast.success(t('accountManager.referralPayouts.attributed', { credits: usd(res?.welcomeCreditCents ?? 0) }));
      setRefereeAccountId('');
      setCode('');
      setNote('');
    },
    onError: (e) => toast.error(e.message),
  });
  const reconcile = trpc.referral.opsReconcile.useMutation({
    onSuccess: (res) => toast.success(t('accountManager.referralPayouts.reconciled', { posted: res.posted, amount: usd(res.postedCents) })),
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Banknote className="h-4 w-4" />
              {t('accountManager.referralPayouts.title')}
            </CardTitle>
            <CardDescription>{t('accountManager.referralPayouts.description')}</CardDescription>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowAll((v) => !v)}>
              {showAll ? t('accountManager.referralPayouts.showOpen') : t('accountManager.referralPayouts.showAll')}
            </Button>
            <Button size="sm" variant="outline" disabled={reconcile.isPending} onClick={() => reconcile.mutate(undefined)}>
              {reconcile.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
              {t('accountManager.referralPayouts.reconcile')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !list.data || list.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('accountManager.referralPayouts.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('accountManager.referralPayouts.requested')}</TableHead>
                  <TableHead>{t('accountManager.referralPayouts.referrer')}</TableHead>
                  <TableHead>{t('accountManager.referralPayouts.bank')}</TableHead>
                  <TableHead className="text-right">{t('accountManager.referralPayouts.amount')}</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((row) => (
                  <SettlementRow key={row.id} row={row as Parameters<typeof SettlementRow>[0]['row']} onDone={() => void refresh()} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4" />
            {t('accountManager.referralPayouts.attributeTitle')}
          </CardTitle>
          <CardDescription>{t('accountManager.referralPayouts.attributeHelp')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="rf-referee">{t('accountManager.referralPayouts.refereeAccountId')}</Label>
            <Input id="rf-referee" value={refereeAccountId} onChange={(e) => setRefereeAccountId(e.target.value)} placeholder="acc_…" className="font-mono" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rf-code">{t('accountManager.referralPayouts.code')}</Label>
            <Input id="rf-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="TGD-XXXXXX" className="font-mono uppercase" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rf-note">{t('accountManager.referralPayouts.note')}</Label>
            <Input id="rf-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button
              disabled={!refereeAccountId.trim() || code.trim().length < 6 || attribute.isPending}
              onClick={() => attribute.mutate({ refereeAccountId: refereeAccountId.trim(), code: code.trim(), note: note || undefined })}
            >
              {t('accountManager.referralPayouts.attribute')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
